/**
 * Fee escalation rules — Settings → Escalation tab.
 * Lets super_admin enable/disable, set counsellor_email, edit/add/remove rules.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash, Plus } from "@phosphor-icons/react";

const ACTIONS = ["reminder", "notify_counsellor", "lock_portal", "tag_defaulter"];
const CHANNELS = ["email", "sms", "whatsapp"];

export default function FeeEscalation() {
  const [cfg, setCfg] = useState({ enabled: false, counsellor_email: "", rules: [] });
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [c, l] = await Promise.all([
      api.get("/escalation/config"),
      api.get("/escalation/log", { params: { limit: 50 } }).catch(() => ({ data: [] })),
    ]);
    setCfg({ ...c.data, counsellor_email: c.data.counsellor_email || "" });
    setLog(l.data || []);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/escalation/config", cfg);
      toast.success("Saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/escalation/run-now");
      toast.success(`Evaluated ${r.data.fees_evaluated} fees · fired ${r.data.actions_fired} actions`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const updateRule = (i, patch) => setCfg((c) => ({ ...c, rules: c.rules.map((r, j) => (i === j ? { ...r, ...patch } : r)) }));
  const addRule = () => setCfg((c) => ({ ...c, rules: [...c.rules, { days_overdue: 60, action: "reminder", channels: ["whatsapp"] }] }));
  const removeRule = (i) => setCfg((c) => ({ ...c, rules: c.rules.filter((_, j) => i !== j) }));
  const toggleChannel = (i, ch) => setCfg((c) => ({
    ...c,
    rules: c.rules.map((r, j) => {
      if (j !== i) return r;
      const chs = new Set(r.channels || []);
      if (chs.has(ch)) chs.delete(ch); else chs.add(ch);
      return { ...r, channels: Array.from(chs) };
    }),
  }));

  return (
    <DashboardLayout>
      <div className="vs-section">
        <h1 className="text-2xl font-semibold">Fee-default escalation</h1>
        <p className="text-sm text-[var(--vs-text-secondary)]">
          Cron-driven policy that escalates unpaid fees through configurable stages: reminder → counsellor → portal lock → defaulter tag. Runs daily as part of the reminders sweep.
        </p>
      </div>

      <div className="vs-card p-5 space-y-4">
        <div className="flex items-center gap-3">
          <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="esc-enabled" />
          <span className="text-sm">Enable engine</span>
        </div>
        <div className="max-w-md">
          <Label>Counsellor email <span className="text-[10px] text-[var(--vs-text-secondary)]">(receives notify_counsellor escalations)</span></Label>
          <Input data-testid="esc-counsellor" type="email" value={cfg.counsellor_email} onChange={(e) => setCfg({ ...cfg, counsellor_email: e.target.value })} className="bg-white" placeholder="counsellor@trust.org" />
        </div>

        <div className="space-y-2">
          <Label>Rules (ordered by days overdue)</Label>
          {cfg.rules.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end p-2 rounded border border-[var(--vs-border)]/40 bg-[var(--vs-bg-subtle)]" data-testid={`esc-rule-${i}`}>
              <div className="col-span-2">
                <Label className="text-[10px]">Days ≥</Label>
                <Input type="number" min="1" value={r.days_overdue} onChange={(e) => updateRule(i, { days_overdue: Number(e.target.value) || 1 })} className="bg-white font-mono" data-testid={`esc-rule-${i}-days`} />
              </div>
              <div className="col-span-3">
                <Label className="text-[10px]">Action</Label>
                <Select value={r.action} onValueChange={(v) => updateRule(i, { action: v })}>
                  <SelectTrigger data-testid={`esc-rule-${i}-action`} className="bg-white capitalize"><SelectValue /></SelectTrigger>
                  <SelectContent>{ACTIONS.map((a) => (<SelectItem key={a} value={a} className="capitalize">{a.replace(/_/g, " ")}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="col-span-6">
                <Label className="text-[10px]">Channels {r.action === "lock_portal" || r.action === "tag_defaulter" ? <span className="text-[var(--vs-text-secondary)]">(N/A)</span> : null}</Label>
                <div className="flex gap-1.5 mt-1">
                  {CHANNELS.map((ch) => {
                    const on = (r.channels || []).includes(ch);
                    const disabled = r.action === "lock_portal" || r.action === "tag_defaulter";
                    return (
                      <button
                        type="button"
                        key={ch}
                        disabled={disabled}
                        onClick={() => toggleChannel(i, ch)}
                        data-testid={`esc-rule-${i}-ch-${ch}`}
                        className={`capitalize text-xs px-2 py-1 rounded border transition ${
                          on ? "bg-[var(--vs-primary)] text-white border-[var(--vs-primary)]" : "bg-white border-[var(--vs-border)]"
                        } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                      >
                        {ch}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="col-span-1 flex justify-end pb-1">
                <Button size="sm" variant="ghost" onClick={() => removeRule(i)} data-testid={`esc-rule-${i}-remove`}><Trash size={14} /></Button>
              </div>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={addRule} data-testid="esc-add-rule"><Plus size={14} className="mr-1.5" /> Add rule</Button>
        </div>

        <div className="flex gap-2">
          <Button data-testid="esc-save" onClick={save} disabled={busy}>Save</Button>
          <Button data-testid="esc-run-now" variant="outline" onClick={runNow} disabled={busy}>Run sweep now</Button>
        </div>

        {cfg.last_run_at && (
          <div className="text-xs text-[var(--vs-text-secondary)] bg-[var(--vs-bg-subtle)] border border-[var(--vs-border)]/40 rounded p-2 font-mono" data-testid="esc-last-run">
            Last run: {cfg.last_run_at} — {JSON.stringify(cfg.last_run_summary)}
          </div>
        )}
      </div>

      <div className="vs-card p-5 mt-4">
        <div className="font-medium mb-2">Recent escalations</div>
        <div className="space-y-1.5">
          {log.map((e) => (
            <div key={e.id} className="text-xs flex items-center justify-between p-2 rounded bg-[var(--vs-bg-subtle)]" data-testid={`esc-log-${e.id}`}>
              <span className="font-mono">{e.fired_at.slice(0, 16)} · {e.student_id.slice(0, 8)}…</span>
              <span className="capitalize">{e.rule_action.replace(/_/g, " ")} ({e.rule_days}d)</span>
              <span className={e.succeeded ? "text-emerald-600" : "text-red-600"}>{e.succeeded ? "ok" : "fail"}</span>
            </div>
          ))}
          {!log.length && <div className="text-xs text-[var(--vs-text-secondary)]">No escalations fired yet.</div>}
        </div>
      </div>
    </DashboardLayout>
  );
}
